/** Column visibility popover with checkboxes. Reads column definitions from DataView context. */
import { useState } from 'react';
import { DialogTrigger, Dialog as AriaDialog } from 'react-aria-components';
import { SlidersHorizontal } from 'lucide-react';
import { Button } from '../Button';
import { Popover } from '../Popover';
import { Checkbox } from '../Checkbox';
import { useDataView } from './DataView';

export function DataViewColumnToggle({ className }: { className?: string }) {
  const { columns, visibleColumns, onVisibleColumnsChange } = useDataView();
  const [isOpen, setIsOpen] = useState(false);

  if (!onVisibleColumnsChange) return null;

  function toggle(key: string, checked: boolean) {
    const next = new Set(visibleColumns);
    if (checked) next.add(key);
    else next.delete(key);
    onVisibleColumnsChange!(next);
  }

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
      <Button
        variant="ghost"
        size="icon"
        aria-label="Toggle columns"
        className={`h-8 w-8 rounded-full ${className ?? ''}`}
        style={{
          color: isOpen ? 'var(--f-accent-text)' : 'var(--f-fg-muted)',
          background: isOpen ? 'var(--f-accent-subtle)' : undefined,
        }}
      >
        <SlidersHorizontal size={16} strokeWidth={2} style={{ transform: 'translateZ(0)' }} />
      </Button>
      <Popover placement="bottom start" className="w-48">
        <AriaDialog className="p-2 outline-none">
          <h3
            className="mb-1 px-2 text-xs font-semibold"
            style={{ color: 'var(--f-fg-muted)', fontFamily: 'var(--f-font-display)' }}
          >
            Columns
          </h3>
          {columns
            .filter((c) => !c.alwaysVisible)
            .map((col) => (
              <Checkbox
                key={col.key}
                isSelected={visibleColumns.has(col.key)}
                onChange={(checked) => toggle(col.key, checked)}
                className="w-full rounded-lg px-2 py-1.5 hover:bg-[var(--f-bg-sunken)]"
              >
                {col.label}
              </Checkbox>
            ))}
        </AriaDialog>
      </Popover>
    </DialogTrigger>
  );
}
